import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../common';
import {
  getGraphQLClient,
  GENERATE_COVER_IMAGE_ASYNC,
  uploadBookCover,
  removeBookCover,
} from '../../lib/graphql';
import { captureException } from '../../lib/sentry';
import { useBackgroundJobsContext } from '../../hooks/useBackgroundJobs';
import { BackgroundJob, CoverSettings } from '../../types';

interface CoverImageSectionProps {
  bookId: string;
  coverImageUrl?: string | null;
  coverImageOriginalUrl?: string | null;
  onCoverChange: (
    url: string | null,
    originalUrl?: string | null,
    settings?: CoverSettings | null
  ) => void;
  disabled?: boolean;
  /** When true, file uploads and removals are deferred - file is stored locally and onPendingFileChange is called */
  deferSave?: boolean;
  /** Called with the file to upload when deferSave is true */
  onPendingFileChange?: (file: File | null) => void;
  /** Preview URL for a pending file upload (created with URL.createObjectURL) */
  pendingPreviewUrl?: string | null;
}

export default function CoverImageSection({
  bookId,
  coverImageUrl,
  coverImageOriginalUrl,
  onCoverChange,
  disabled = false,
  deferSave = false,
  onPendingFileChange,
  pendingPreviewUrl,
}: CoverImageSectionProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isGenerating, setIsGenerating] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [backgroundJobId, setBackgroundJobId] = useState<string | null>(null);
  const [localOriginalUrl, setLocalOriginalUrl] = useState<string | null>(
    coverImageOriginalUrl || null
  );
  const [customInstructions, setCustomInstructions] = useState('');

  const { activeJobs, trackJob, getJobForTarget } = useBackgroundJobsContext();

  // Check for existing background job on mount
  useEffect(() => {
    const existingJob = getJobForTarget('Book', bookId);
    if (existingJob && existingJob.jobType === 'generate_cover_image') {
      setBackgroundJobId(existingJob.id);
      setIsGenerating(true);
    }
  }, [bookId, getJobForTarget]);

  // Watch for job completion
  useEffect(() => {
    if (!backgroundJobId) return;

    const job = activeJobs.find((j) => j.id === backgroundJobId);
    if (!job) return;

    if (job.status === 'completed') {
      const result = job.result as { cover_image_url?: string; cover_image_original_url?: string };
      if (result?.cover_image_url) {
        onCoverChange(result.cover_image_url, result.cover_image_original_url || null, null);
        if (result.cover_image_original_url) {
          setLocalOriginalUrl(result.cover_image_original_url);
        }
      }
      setBackgroundJobId(null);
      setIsGenerating(false);
    } else if (job.status === 'failed') {
      setError(job.errorMessage || t('common.error'));
      setBackgroundJobId(null);
      setIsGenerating(false);
    }
  }, [activeJobs, backgroundJobId, onCoverChange, t]);

  const handleGenerateWithAI = async () => {
    setIsGenerating(true);
    setError(null);

    try {
      const client = getGraphQLClient();

      // Always use async mode for cover image generation (it's heavy)
      const data = await client.request<{
        generateCoverImage: {
          coverImageUrl: string | null;
          backgroundJob?: BackgroundJob;
          errors: string[];
        };
      }>(GENERATE_COVER_IMAGE_ASYNC, {
        bookId,
        customInstructions: customInstructions.trim() || null,
      });

      if (data.generateCoverImage.errors.length > 0) {
        setError(data.generateCoverImage.errors[0]);
        setIsGenerating(false);
      } else if (data.generateCoverImage.backgroundJob) {
        const job = data.generateCoverImage.backgroundJob;
        setBackgroundJobId(job.id);
        trackJob(job);
        // Keep isGenerating true, will be set false when job completes
      } else if (data.generateCoverImage.coverImageUrl) {
        onCoverChange(data.generateCoverImage.coverImageUrl);
        setIsGenerating(false);
      }
    } catch (err) {
      captureException(err);
      setError(t('common.error'));
      setIsGenerating(false);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ['image/png', 'image/jpeg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError(t('books.invalidImageType'));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setError(t('books.imageTooLarge'));
      return;
    }

    setError(null);

    // In deferred mode, store file locally and show preview without uploading
    if (deferSave && onPendingFileChange) {
      onPendingFileChange(file);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    setIsUploading(true);

    try {
      const result = await uploadBookCover(bookId, file);

      if (result.errors && result.errors.length > 0) {
        setError(result.errors[0]);
      } else if (result.coverImageUrl) {
        onCoverChange(result.coverImageUrl, result.coverImageOriginalUrl || null, null);
        if (result.coverImageOriginalUrl) {
          setLocalOriginalUrl(result.coverImageOriginalUrl);
        }
      }
    } catch (err) {
      captureException(err);
      setError(t('common.error'));
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveCover = async () => {
    setError(null);

    // In deferred mode, just clear locally without server call
    if (deferSave) {
      onCoverChange(null, null, null);
      setLocalOriginalUrl(null);
      if (onPendingFileChange) {
        onPendingFileChange(null);
      }
      return;
    }

    setIsRemoving(true);

    try {
      await removeBookCover(bookId);
      onCoverChange(null, null, null);
      setLocalOriginalUrl(null);
    } catch (err) {
      captureException(err);
      setError(t('common.error'));
    } finally {
      setIsRemoving(false);
    }
  };

  const handleOpenEditor = () => {
    navigate(`/books/${bookId}/cover-editor`);
  };

  const isLoading = isGenerating || isUploading || isRemoving;

  // Check if we can open the editor (need original image OR current cover image)
  const editableImageUrl = localOriginalUrl || coverImageOriginalUrl || coverImageUrl;
  const canEditCover = !!editableImageUrl;

  return (
    <div className="space-y-3">
      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
        {t('books.coverImage')}
        <span className="text-gray-400 dark:text-gray-500 font-normal ml-2">
          ({t('common.optional')})
        </span>
      </label>

      <div className="flex flex-col sm:flex-row gap-4">
        {/* Cover Preview */}
        <div className="relative w-32 h-48 bg-gradient-to-br from-primary-500/20 via-purple-500/20 to-accent-500/20 rounded-lg overflow-hidden border-2 border-dashed border-gray-300 dark:border-gray-600 flex-shrink-0">
          {pendingPreviewUrl || coverImageUrl ? (
            <>
              <img
                src={pendingPreviewUrl || coverImageUrl || ''}
                alt={t('books.coverImage')}
                className="w-full h-full object-cover"
              />
              {isLoading && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <svg className="w-8 h-8 text-white animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                </div>
              )}
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-400 dark:text-gray-500">
              {isLoading ? (
                <svg className="w-8 h-8 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              ) : (
                <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              )}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-2 justify-center">
          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={handleFileSelect}
            className="hidden"
            disabled={disabled || isLoading}
          />

          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled || isLoading}
            isLoading={isUploading}
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
              />
            </svg>
            {t('books.uploadCover')}
          </Button>

          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleGenerateWithAI}
            disabled={disabled || isLoading}
            isLoading={isGenerating}
          >
            <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 10V3L4 14h7v7l9-11h-7z"
              />
            </svg>
            {t('ai.generateWithAi')}
          </Button>

          {canEditCover && (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handleOpenEditor}
              disabled={disabled || isLoading}
            >
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              {t('books.editCover')}
            </Button>
          )}

          {(pendingPreviewUrl || coverImageUrl) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleRemoveCover}
              disabled={disabled || isLoading}
              isLoading={isRemoving}
              className="text-red-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
            >
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              {t('books.removeCover')}
            </Button>
          )}
        </div>
      </div>

      {/* Error Display */}
      <AnimatePresence>
        {error && (
          <motion.p
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="text-sm text-red-500"
          >
            {error}
          </motion.p>
        )}
      </AnimatePresence>

      <p className="text-xs text-gray-500 dark:text-gray-400">{t('books.coverImageHint')}</p>

      {/* Custom Instructions */}
      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {t('books.coverCustomInstructions')}
          <span className="text-gray-400 dark:text-gray-500 font-normal ml-2">
            ({t('common.optional')})
          </span>
        </label>
        <textarea
          value={customInstructions}
          onChange={(e) => setCustomInstructions(e.target.value)}
          placeholder={t('books.coverCustomInstructionsPlaceholder')}
          disabled={disabled || isLoading}
          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed resize-none"
          rows={2}
          maxLength={500}
        />
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {t('books.coverCustomInstructionsHint')}
        </p>
      </div>
    </div>
  );
}
